import * as React from 'react';
// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import props.
import {
	InputFormProps,
	InputNumberWithoutInputTextBoxProps,
} from '@app/props';

interface UseInputNumberParameters {
	id: string;
	label: string;
	validationLabel?: string;
	infoText?: string;
	required: boolean;
	disabled: boolean;
	value?: number;
	maxValue?: number;
	maxValueLabel?: string;
	maxValueInclusive?: boolean;
	minValue?: number;
	minValueLabel?: string;
	minValueInclusive?: boolean;
	showValidation?: boolean;
	validators?: ((input: number | undefined, required: boolean) => ValidatorResponse)[]; 
}

type UseInputNumberResults = UseInputFormResult<number | undefined, InputNumberWithoutInputTextBoxProps>

const useInputNumber = (params: UseInputNumberParameters): UseInputNumberResults => {

	// #region exported methods

	/**
	 * Returns props for form input. 
	 **/
	const applyInputNumberProps = (): InputNumberWithoutInputTextBoxProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the number props.
		return {
			...formProps,
			infoText: params.infoText,
			onChange: other.setInput,
			value: other.value,
		}
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * 
	 * @param value
	 * @param required
	 */
	const validateNumber_UndefinedChecks = (value: number | undefined, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (required && value === undefined) {
			let validationLabel: string = !String.isNullOrWhiteSpace(params.validationLabel) ? `The ${params.validationLabel}` : 'An answer';
			response = new ValidatorResponse(false, `${validationLabel} is required.`);
		}

		return response;
	}

	/**
	 * Validates the current value.
	 * @param value
	 * @param required
	 */
	const validateNumber_BoundaryCheck = (value: number | undefined, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (value !== undefined) {
			if (params.maxValue !== undefined) {
				// In the case where the max value is exceeded.
				let validationLabel: string = !String.isNullOrWhiteSpace(params.validationLabel) ? `The ${params.validationLabel}` : 'The value';
				let maxValueStr: string = params.maxValue.toString();
				let maxValueLabel: string = !String.isNullOrWhiteSpace(params.maxValueLabel)
					? `the ${params.maxValueLabel} of ${maxValueStr}`
					: maxValueStr;

				if ((params.maxValueInclusive ?? false) && value > params.maxValue) {
					response = new ValidatorResponse(false, `${validationLabel} cannot be greater than ${maxValueLabel}.`);
				}
				else if (!params.maxValueInclusive && value >= params.maxValue) {
					response = new ValidatorResponse(false, `${validationLabel} cannot be greater than or equal to ${maxValueLabel}.`);
				}
			}
			if (params.minValue !== undefined) {
				// In the case where the min value is exceeded.
				let validationLabel: string = !String.isNullOrWhiteSpace(params.validationLabel) ? `The ${params.validationLabel}` : 'The value';
				let minValueStr: string = params.minValue.toString();
				let minValueLabel: string = !String.isNullOrWhiteSpace(params.minValueLabel)
					? `the ${params.minValueLabel} of ${minValueStr}`
					: minValueStr;

				if ((params.minValueInclusive ?? false) && value < params.minValue) {
					response = new ValidatorResponse(false, `${validationLabel} cannot be less than ${minValueLabel}.`);
				}
				else if (!params.minValueInclusive && value <= params.minValue) {
					response = new ValidatorResponse(false, `${validationLabel} cannot be less than or equal to ${minValueLabel}.`);
				}
			}
		}

		return response;
	}

	// #endregion private methods


	// #region variables

	const validators = React.useMemo(() => params.validators ?? [], [params.validators]);

	// Instantiate the 'number' input.
	const { applyProps, ...other } = useInputForm<number | undefined>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		initialInput: params.value,
		showValidation: params.showValidation,
		validators: [validateNumber_UndefinedChecks, validateNumber_BoundaryCheck, ...validators],
	});

	// #endregion variables


	return {
		applyProps: applyInputNumberProps,
		...other,
	};
}

// Export parameters.
export {
	UseInputNumberParameters,
}

// Export results.
export {
	UseInputNumberResults,
}

export default useInputNumber;